import { useState } from 'react';
import type { DriverRow, PickupPointRow, ZoneRow } from '../lib/types';
import { Download, X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

export interface Filters {
  q: string;
  driverId: string;
  zoneId: string;
  pickupPointId: string;
  needsReview: boolean;
  /** 只看「代收金额还没补上」的 —— 这些单送完就沉下去了,身上还欠一个数 */
  codPendingOnly: boolean;
  /** 只看小票还没打过的 —— 仓库贴标签时每天要点几十次 */
  unprintedOnly: boolean;
  createdFrom: string;
  createdTo: string;
}

export const EMPTY_FILTERS: Filters = {
  q: '',
  driverId: '',
  zoneId: '',
  pickupPointId: '',
  needsReview: false,
  codPendingOnly: false,
  unprintedOnly: false,
  createdFrom: '',
  createdTo: '',
};

/** 有几个条件在生效 —— 折叠起来时要让人知道「还筛着东西」 */
export function activeCount(f: Filters): number {
  return [
    f.driverId,
    f.zoneId,
    f.pickupPointId,
    f.needsReview ? '1' : '',
    f.codPendingOnly ? '1' : '',
    f.unprintedOnly ? '1' : '',
    f.createdFrom,
    f.createdTo,
  ].filter(Boolean).length;
}

/**
 * 订单筛选。
 *
 * ★ 这些条件后端**一直都支持**,只是界面没用上 ——
 *   调度员一直靠搜索框和状态页签硬找。
 *
 * 默认折叠:大多数时候调度员只用状态页签,展开一堆下拉框会挤掉列表空间。
 * 但折叠时必须显示「已筛 N 项」,否则会出现「怎么少了一半单」的困惑。
 */
export function OrderFilters({
  value,
  onChange,
  zones,
  drivers,
  points,
  onExport,
  canExport,
  exporting,
}: {
  value: Filters;
  onChange: (f: Filters) => void;
  zones: ZoneRow[];
  drivers: DriverRow[];
  points: PickupPointRow[];
  onExport: () => void;
  canExport: boolean;
  exporting: boolean;
}) {
  const n = activeCount(value);
  const [open, setOpen] = useState(n > 0);
  const set = (patch: Partial<Filters>) => onChange({ ...value, ...patch });

  /**
   * 现在生效的条件,一条一个。
   *
   * ★ 名字要**人看得懂**,不是 id:「司机:张三」而不是一串 ULID。
   *   而司机/区域/取货点的名字只有传进来的那几份列表里有 ——
   *   查不到就退回原值,总比显示空白强。
   */
  const chips: Array<{ key: string; text: string; clear: Partial<Filters> }> = [];
  if (value.driverId) {
    const d = drivers.find((x) => x.id === value.driverId);
    chips.push({
      key: 'driver',
      text: `司机:${d ? d.name : value.driverId}`,
      clear: { driverId: '' },
    });
  }
  if (value.zoneId) {
    const z = zones.find((x) => x.id === value.zoneId);
    chips.push({ key: 'zone', text: `区域:${z ? z.name : value.zoneId}`, clear: { zoneId: '' } });
  }
  if (value.pickupPointId) {
    const p = points.find((x) => x.id === value.pickupPointId);
    chips.push({
      key: 'point',
      text: `取货点:${p ? p.name : value.pickupPointId}`,
      clear: { pickupPointId: '' },
    });
  }
  if (value.createdFrom || value.createdTo) {
    chips.push({
      key: 'date',
      // 只填一头也要说得清是哪一头 —— 「8/1 起」和「到 8/15」是两回事
      text: value.createdFrom && value.createdTo
        ? `${value.createdFrom} ~ ${value.createdTo}`
        : value.createdFrom
          ? `${value.createdFrom} 起`
          : `到 ${value.createdTo}`,
      clear: { createdFrom: '', createdTo: '' },
    });
  }
  if (value.needsReview) {
    chips.push({ key: 'review', text: '只看待复核', clear: { needsReview: false } });
  }
  if (value.codPendingOnly) {
    chips.push({ key: 'codp', text: '只看代收待补金额', clear: { codPendingOnly: false } });
  }
  if (value.unprintedOnly) {
    chips.push({ key: 'unprinted', text: '只看没打过小票的', clear: { unprintedOnly: false } });
  }

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={value.q}
          onChange={(e) => set({ q: e.target.value })}
          placeholder="单号 / 收件人 / 手机号"
          className="w-72 h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700
                     bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
        />

        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={`h-10 px-3 rounded-lg border text-sm ${
            n > 0
              ? 'border-brand-500 text-brand-600 dark:text-brand-500 bg-brand-50 dark:bg-brand-500/10'
              : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400'
          }`}
        >
          {/*
            ★ 按钮上点明**里面有什么**。
              原来只写「筛选」两个字 —— 而这一屏上方还有状态标签,
              人会以为「筛选」就是那个。用户报的
              「增加根据司机过滤订单的功能」「支持用时间跨度搜索」
              要的两样其实一直都在这里面,只是没人知道。
          */}
          {open ? '▾' : '▸'} 筛选{n > 0 ? ` · ${n}` : ':司机 / 时间 / 区域'}
        </button>

        {/*
          ★ 生效的条件一条条摆出来,而且能单独去掉。
            收起来之后只有一个「· 2」,人看着一个被过滤过的列表
            却不知道被过滤掉了什么 —— 「这单怎么不见了」多半就是这么来的。
        */}
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => set(c.clear)}
            aria-label={`去掉筛选:${c.text}`}
            className="h-8 rounded-full border border-brand-300 bg-brand-50 px-2.5 text-xs
                       text-brand-700 dark:border-brand-800 dark:bg-brand-500/10
                       dark:text-brand-400"
          >
            {c.text} <InlineIcon icon={X} />
          </button>
        ))}

        {n > 0 && (
          <button
            onClick={() => onChange({ ...EMPTY_FILTERS, q: value.q })}
            className="h-10 px-3 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            清除筛选
          </button>
        )}

        <div className="flex-1" />

        {canExport && (
          <button
            onClick={onExport}
            disabled={exporting}
            title="按当前筛选条件导出 CSV"
            className="h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600
                       text-sm text-slate-700 dark:text-slate-300 disabled:opacity-50"
          >
            {exporting ? '导出中…' : <><InlineIcon icon={Download} /> 导出 CSV</>}
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-3 gap-3 rounded-xl border
                        border-slate-200 dark:border-slate-700 p-4
                        bg-slate-50 dark:bg-slate-800/50">
          <Field label="司机">
            <Select
              label="司机"
              value={value.driverId}
              onChange={(v) => set({ driverId: v })}
              options={drivers.map((d) => ({ value: d.id, label: `${d.name}(${d.driverNo})` }))}
            />
          </Field>

          <Field label="区域">
            <Select
              label="区域"
              value={value.zoneId}
              onChange={(v) => set({ zoneId: v })}
              options={zones.map((z) => ({ value: z.id, label: `${z.code} ${z.name}` }))}
            />
          </Field>

          <Field label="取货点">
            <Select
              label="取货点"
              value={value.pickupPointId}
              onChange={(v) => set({ pickupPointId: v })}
              options={points.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Field>

          <Field label="创建时间(起)">
            <input
              type="date"
              aria-label="创建时间(起)"
              value={value.createdFrom}
              onChange={(e) => set({ createdFrom: e.target.value })}
              className={inputCls}
            />
          </Field>

          <Field label="创建时间(止)">
            <input
              type="date"
              aria-label="创建时间(止)"
              value={value.createdTo}
              onChange={(e) => set({ createdTo: e.target.value })}
              className={inputCls}
            />
          </Field>

          <Field label="其他">
            <label className="flex items-center gap-2 h-10 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={value.needsReview}
                onChange={(e) => set({ needsReview: e.target.checked })}
              />
              只看待复核(缺坐标/区域存疑)
            </label>
            {/*
              ★ 代收待补金额的单送完就沉进历史里了,而它们身上还欠一个数。
                没有一个地方能把它们捞出来的话,这笔钱再也没人回来记。
            */}
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={value.codPendingOnly}
                onChange={(e) => set({ codPendingOnly: e.target.checked })}
              />
              只看代收待补金额
            </label>
            {/*
              ★ 仓库贴标签的动作是:筛出没打过的 → 全选 → 打印 →
                这一批从列表里消失。没有这个筛选的话,批量选的时候
                分不清哪些打过,同一批会被打第二遍 —— 而纸已经贴在框上了。
            */}
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={value.unprintedOnly}
                onChange={(e) => set({ unprintedOnly: e.target.checked })}
              />
              只看没打过小票的
            </label>
          </Field>
        </div>
      )}
    </div>
  );
}

const inputCls =
  'w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs text-slate-500 dark:text-slate-400">{label}</div>
      {children}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    >
      <option value="">全部</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
